import { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Store, Loader2 } from "lucide-react";
import { getImageUrl } from "../../utils/imageUtils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const publicAxios = axios.create({ withCredentials: false });

export default function RelatedProducts({ currentProductId, categoryId, subdomain, theme }) {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchRelated = async () => {
            if (!categoryId) return;
            setLoading(true);
            try {
                // Fetch products from same category, excluding current
                let url = `${API}/products?category_id=${categoryId}&limit=5`;
                if (subdomain) {
                    url = `${API}/public/store/${subdomain}/products?category_id=${categoryId}&limit=5`;
                }

                const res = await publicAxios.get(url);
                const all = res.data.products || res.data || [];

                // Filter out current product and limit to 4
                const related = all
                    .filter(p => p.product_id !== currentProductId)
                    .slice(0, 4);

                setProducts(related);
            } catch (error) {
                console.error("Failed to fetch related products", error);
            } finally {
                setLoading(false);
            }
        };

        fetchRelated();
    }, [categoryId, currentProductId, subdomain]);

    if (loading) return <div className="flex justify-center py-10"><Loader2 className="animate-spin opacity-50" /></div>;
    if (products.length === 0) return null;

    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {products.map((product) => (
                <div
                    key={product.product_id}
                    className="group bg-white rounded-2xl p-4 shadow-sm hover:shadow-xl transition-all duration-300 border border-slate-50 flex flex-col"
                >
                    <div className="relative aspect-[4/5] rounded-xl overflow-hidden bg-slate-100 mb-4">
                        <Link to={subdomain ? `/store/${subdomain}/product/${product.product_id}` : `/product/${product.product_id}`}>
                            {product.image_url || product.images?.[0] ? (
                                <img
                                    src={getImageUrl(product.image_url || product.images?.[0])}
                                    alt={product.name}
                                    className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
                                    loading="lazy"
                                />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center bg-slate-200 text-slate-400">
                                    <Store size={32} />
                                </div>
                            )}
                        </Link>
                    </div>

                    <div className="space-y-2 flex-grow flex flex-col">
                        <Link
                            to={subdomain ? `/store/${subdomain}/product/${product.product_id}` : `/product/${product.product_id}`}
                            className="block flex-grow"
                        >
                            <h3
                                className="font-semibold line-clamp-2 transition-colors group-hover:text-amber-700"
                                style={{ fontSize: '1.05rem', color: theme.primary_color }}
                            >
                                {product.name}
                            </h3>
                        </Link>
                        <div className="pt-2">
                            <span className="font-bold text-lg" style={{ color: theme.accent_color }}>
                                ${(product.display_price || product.calculated_price || product.base_price || 0).toFixed(2)}
                            </span>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
}
